import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, GraduationCap, Brain, CheckCircle, Sparkle,
  BookOpen, Video, Certificate, ChartLineUp, Target,
  Clock, Users, Rocket, Lightning, ShieldCheck,
  Play, Trophy, Star, Lightbulb
} from "@phosphor-icons/react";

const AITrainingPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: Brain,
      title: "Adaptive Learning Engine",
      description: "AI adjusts content difficulty and pace based on individual performance, ensuring optimal learning outcomes for every team member."
    },
    {
      icon: Video,
      title: "Interactive Simulations",
      description: "Practice clinical scenarios in safe, virtual environments with AI-guided feedback and remediation."
    },
    {
      icon: Target,
      title: "Competency Mapping",
      description: "Track skill development across your entire organization with detailed analytics and progress reports."
    },
    {
      icon: Certificate,
      title: "Auto-Certification",
      description: "Automatically generate and track certifications, CEUs, and compliance training with audit-ready documentation."
    },
    {
      icon: BookOpen,
      title: "Content Library",
      description: "Access 200+ hospice-specific courses covering clinical, compliance, leadership, and soft skills."
    },
    {
      icon: Users,
      title: "Team Learning",
      description: "Create cohort-based learning experiences with group discussions, challenges, and collaborative projects."
    }
  ];

  const courseCategories = [
    {
      title: "Clinical Excellence",
      courses: ["Pain & Symptom Management", "End-of-Life Care", "Wound Care", "Medication Management", "Patient Assessment"],
      color: "bg-emerald-500"
    },
    {
      title: "Compliance & Regulatory",
      courses: ["CMS CoPs Mastery", "CHAP Standards", "HIPAA Essentials", "Documentation Best Practices", "Survey Preparation"],
      color: "bg-blue-500"
    },
    {
      title: "Leadership Development",
      courses: ["New Manager Bootcamp", "Team Building", "Conflict Resolution", "Performance Management", "Change Leadership"],
      color: "bg-purple-500"
    },
    {
      title: "Soft Skills",
      courses: ["Therapeutic Communication", "Cultural Competency", "Family Support", "Grief & Bereavement", "Self-Care for Caregivers"],
      color: "bg-orange-500"
    }
  ];

  const outcomes = [
    { metric: "94%", label: "Course Completion Rate" },
    { metric: "87%", label: "Knowledge Retention" },
    { metric: "45%", label: "Reduction in Training Time" },
    { metric: "100%", label: "Audit Compliance" }
  ];

  const learningModes = [
    {
      icon: Video,
      title: "Video Courses",
      description: "Bite-sized video lessons with expert instructors"
    },
    {
      icon: BookOpen,
      title: "Interactive Modules",
      description: "Hands-on exercises with immediate feedback"
    },
    {
      icon: Play,
      title: "Clinical Simulations",
      description: "Practice scenarios in safe virtual environments"
    },
    {
      icon: Users,
      title: "Live Sessions",
      description: "Real-time workshops with industry experts"
    }
  ];

  return (
    <div data-testid="ai-training-page">
      {/* Hero Section */}
      <section className="section-padding bg-hero-gradient relative overflow-hidden">
        <div className="decorative-blob w-96 h-96 bg-primary/20 -top-48 -right-48" />
        <div className="decorative-blob w-72 h-72 bg-accent/20 bottom-0 left-0" />
        
        <div className="max-w-7xl mx-auto container-padding relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <span className="badge badge-primary mb-6">
                <GraduationCap weight="fill" className="w-4 h-4 mr-2" />
                AI Training & Education
              </span>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-foreground mb-6 leading-tight">
                Elevate Your Team with{" "}
                <span className="text-gradient">Intelligent Learning</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8">
                Our AI-powered training platform delivers personalized education 
                that adapts to each learner, tracks competencies, and ensures 
                your entire team stays survey-ready and clinically excellent.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/schedule-demo"
                  className="btn-primary inline-flex items-center justify-center gap-2"
                  data-testid="training-cta-demo"
                >
                  <Rocket weight="fill" className="w-5 h-5" />
                  Schedule a Demo
                </Link>
                <Link
                  to="/contact"
                  className="btn-secondary inline-flex items-center justify-center gap-2"
                >
                  Talk to an Expert
                  <ArrowRight weight="bold" className="w-5 h-5" />
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white rounded-3xl p-8 shadow-xl border border-border">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
                    <Trophy weight="fill" className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground">Learning Dashboard</h3>
                    <p className="text-sm text-muted-foreground">Your team's progress</p>
                  </div>
                </div>
                
                {/* Sample Course Cards */}
                <div className="space-y-4">
                  {[
                    { title: "CMS CoPs Mastery", progress: 100, badge: "Completed", badgeColor: "bg-green-100 text-green-700" },
                    { title: "Pain Management Advanced", progress: 75, badge: "In Progress", badgeColor: "bg-blue-100 text-blue-700" },
                    { title: "CHAP Survey Prep", progress: 40, badge: "Started", badgeColor: "bg-amber-100 text-amber-700" }
                  ].map((course, i) => (
                    <div key={i} className="bg-muted/50 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-semibold text-foreground text-sm">{course.title}</p>
                        <span className={`text-xs px-2 py-1 rounded-full ${course.badgeColor}`}>
                          {course.badge}
                        </span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            course.progress === 100 ? "bg-green-500" : "bg-primary"
                          }`}
                          style={{ width: `${course.progress}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 pt-6 border-t border-border">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Team Average</span>
                    <span className="font-bold text-primary">87% Complete</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Outcomes Stats */}
      <section className="py-16 bg-primary">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {outcomes.map((outcome, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <p className="font-display text-4xl md:text-5xl text-white mb-2">{outcome.metric}</p>
                <p className="text-white/70">{outcome.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Modes */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto container-padding">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="badge badge-accent mb-4">Multiple Learning Formats</span>
            <h2 className="font-display text-3xl md:text-4xl text-foreground mb-4">
              Learn Your Way
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Different people learn differently. Our platform supports multiple 
              learning modes to maximize engagement and retention.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {learningModes.map((mode, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl p-6 border border-border text-center hover:border-primary/30 hover:shadow-lg transition-all"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <mode.icon weight="duotone" className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-bold text-foreground mb-2">{mode.title}</h3>
                <p className="text-sm text-muted-foreground">{mode.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="section-padding bg-muted/50">
        <div className="max-w-7xl mx-auto container-padding">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="badge badge-primary mb-4">Platform Features</span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
              Powerful Training Technology
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Everything you need to build a culture of continuous learning and excellence.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl p-8 border border-border hover:border-primary/30 hover:shadow-lg transition-all"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                  <feature.icon weight="duotone" className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-bold text-xl text-foreground mb-3">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Categories */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto container-padding">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="badge badge-accent mb-4">200+ Courses</span>
            <h2 className="font-display text-3xl md:text-4xl text-foreground mb-4">
              Comprehensive Course Library
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From clinical excellence to leadership development, we cover every aspect 
              of hospice education.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {courseCategories.map((category, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl overflow-hidden border border-border"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className={`${category.color} px-6 py-4`}>
                  <h3 className="font-bold text-white text-lg">{category.title}</h3>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    {category.courses.map((course, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <CheckCircle weight="fill" className="w-5 h-5 text-primary flex-shrink-0" />
                        <span className="text-foreground">{course}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Adaptive Section */}
      <section className="section-padding bg-muted/50">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="badge badge-primary mb-6">AI-Powered</span>
              <h2 className="font-display text-3xl md:text-4xl text-foreground mb-6">
                Learning That Adapts to You
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Our AI engine continuously analyzes performance, identifies knowledge gaps, 
                and automatically adjusts content to optimize learning outcomes for each 
                individual team member.
              </p>
              
              <div className="space-y-4">
                {[
                  { icon: Lightbulb, text: "Identifies knowledge gaps automatically" },
                  { icon: Target, text: "Recommends personalized learning paths" },
                  { icon: ChartLineUp, text: "Tracks progress and predicts outcomes" },
                  { icon: Clock, text: "Optimizes study time for best results" }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <item.icon weight="duotone" className="w-5 h-5 text-primary" />
                    </div>
                    <span className="text-foreground">{item.text}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-white rounded-3xl p-8 shadow-xl border border-border">
                <div className="flex items-center gap-3 mb-6">
                  <Brain weight="duotone" className="w-8 h-8 text-primary" />
                  <span className="font-bold text-foreground">AI Learning Insights</span>
                </div>
                
                <div className="space-y-6">
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <p className="text-sm font-semibold text-green-800 mb-1">Strength Identified</p>
                    <p className="text-sm text-green-700">Sarah excels in symptom management (92% mastery)</p>
                  </div>
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <p className="text-sm font-semibold text-amber-800 mb-1">Recommended Focus</p>
                    <p className="text-sm text-amber-700">Team needs more training on documentation compliance</p>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <p className="text-sm font-semibold text-blue-800 mb-1">Upcoming Certification</p>
                    <p className="text-sm text-blue-700">3 team members due for annual HIPAA renewal</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section section-padding">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 border border-white rounded-full" />
          <div className="absolute bottom-10 right-10 w-48 h-48 border border-white rounded-full" />
        </div>
        
        <div className="relative max-w-4xl mx-auto container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Sparkle weight="fill" className="w-12 h-12 text-white/80 mx-auto mb-6" />
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-white mb-6">
              Ready to Transform Your Training?
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8">
              See how our AI-powered training platform can elevate your team's 
              skills, ensure compliance, and drive better patient outcomes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/schedule-demo"
                className="bg-white text-primary font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:shadow-xl hover:scale-105 inline-flex items-center justify-center gap-2"
              >
                <Lightning weight="fill" className="w-5 h-5" />
                Schedule a Demo
              </Link>
              <Link
                to="/resources"
                className="border-2 border-white text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:bg-white/10 inline-flex items-center justify-center gap-2"
              >
                Download Training Guide
                <ArrowRight weight="bold" className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AITrainingPage;
